import React, { useState, useContext, useEffect } from 'react';
import { Receipt, ChefHat, User, ConciergeBell, Phone, X, Award, Star, Eye } from 'lucide-react';
import KOTOrders from '../KOT/KOTOrders';
import BillSummary from './BillSummary';
import DraftKOT from '../KOT/DraftKOT';
import { CartContext } from '../../../context/CartContext';
import { useKOT } from '../../../hooks/useKOT';
import { useTableSplits } from '../../../hooks/useTableSplits';
import { tableSplitService } from '../../../services/tableSplitService';
import CustomerSearchModal from '../Modals/CustomerSearchModal';
import WaiterSelectionModal from '../Modals/WaiterSelectionModal';
import CustomerDetailsModal from '../../shared/CustomerDetailsModal';
import Tooltip, { RichTooltip } from '../../common/Tooltip';
import { toast } from 'react-hot-toast';
import { useMenuItems } from '../../../hooks/useMenuItems';

export default function OrderSummary({ tableOrders, onShowRecentOrders }) {
  const [activeTab, setActiveTab] = useState('kots');
  const {
    currentTableId,
    currenttableSplit,
    linkedCustomer,
    linkedWaiter,
    setLinkedCustomer,
    setLinkedWaiter
  } = useContext(CartContext);

  // Modal states
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showWaiterModal, setShowWaiterModal] = useState(false);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const { items: menuItems } = useMenuItems();

  // Get splits for waiter assignment
  const { splits, fetchSplits } = useTableSplits();

  // Fetch splits when table changes (needed for waiter assignment)
  useEffect(() => {
    if (currentTableId) {
      fetchSplits(currentTableId);
    }
  }, [currentTableId]);

  // ✅ Get active KOTs from API
  const tableSplit = currenttableSplit || 'A';
  const { activeOrders, loading, error } = useKOT(
    currentTableId,
    tableSplit,
    { skipIfNew: true }
  );

  // Handle waiter assignment with backend API call
  const handleAssignWaiter = async (waiter) => {
    try {
      // Find the current split
      const tableSplitValue = currenttableSplit || 'A';

      console.log('🔍 Assigning waiter - tableSplitValue:', tableSplitValue);
      console.log('🔍 Available splits:', splits);

      const currentSplitObj = splits.find((s) => {
        const splitName = s.splitName?.trim()?.toLowerCase() || '';
        const orderSplit = tableSplitValue?.trim()?.toLowerCase() || '';
        return splitName === orderSplit ||
               splitName === `split ${orderSplit}` ||
               splitName.endsWith(` ${orderSplit}`);
      });

      console.log('🔍 Found split:', currentSplitObj);

      if (!currentSplitObj) {
        // If split not found, just update local state
        console.warn('⚠️ Split not found, updating local state only');
        setLinkedWaiter(waiter);
        setShowWaiterModal(false);
        toast.success(`Waiter ${waiter.name} assigned!`, { duration: 2000, icon: '👨‍🍳' });
        return;
      }

      // Update table split with waiterId using the updateSplit API
      const updatePayload = {
        ...currentSplitObj,
        waiterId: waiter.id,
      };
      console.log('📤 Calling updateSplit API with:', {
        tableId: currentTableId,
        splitName: currentSplitObj.splitName,
        payload: updatePayload
      });

      await tableSplitService.updateSplit(currentTableId, currentSplitObj.splitName, updatePayload);

      console.log('✅ API call successful');

      // Update local state
      setLinkedWaiter(waiter);

      // Refresh splits to get updated data
      await fetchSplits(currentTableId);

      toast.success(`Waiter ${waiter.name} assigned!`, {
        duration: 2000,
        icon: '👨‍🍳'
      });

      setShowWaiterModal(false);
    } catch (error) {
      console.error('❌ Failed to assign waiter:', error);
      // Still update local state even if API fails
      setLinkedWaiter(waiter);
      setShowWaiterModal(false);
      toast.error('Failed to save waiter to server');
    }
  };

  // Handle waiter unlink with backend API call
  const handleUnlinkWaiter = async () => {
    try {
      // Find the current split
      const tableSplitValue = currenttableSplit || 'A';
      const currentSplitObj = splits.find((s) => {
        const splitName = s.splitName?.trim()?.toLowerCase() || '';
        const orderSplit = tableSplitValue?.trim()?.toLowerCase() || '';
        return splitName === orderSplit ||
               splitName === `split ${orderSplit}` ||
               splitName.endsWith(` ${orderSplit}`);
      });

      if (currentSplitObj) {
        // Update table split to remove waiterId
        await tableSplitService.updateSplit(currentTableId, currentSplitObj.splitName, {
          ...currentSplitObj,
          waiterId: null,
        });

        // Refresh splits
        await fetchSplits(currentTableId);
      }

      // Update local state
      setLinkedWaiter(null);
    } catch (error) {
      console.error('Failed to unlink waiter:', error);
      // Still update local state even if API fails
      setLinkedWaiter(null);
    }
  };

  // ✅ Extract all items from all active KOTs (exclude cancelled orders)
  const allBillItems = (activeOrders || [])
    .filter(kot => kot.status !== 'cancelled') // ✅ Exclude cancelled KOTs
    .flatMap(kot =>
      (kot.items || []).map(item => ({
        ...item,
        kotNumber: kot.kotNumber,
        kotTime: kot.createdAt
      }))
    );

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {/* Header Section */}
      <div className="bg-white border-b-2 border-slate-200 flex-shrink-0">
        <div className="px-4 pt-3">
          {/* Title Row with Customer & Waiter - All in one compact line */}
          <div className="flex items-center justify-between mb-2">
            {/* Left: Title */}
            <h2 className="text-sm font-bold text-slate-500 flex-shrink-0">Order Summary</h2>

            {/* Right: Customer & Waiter compact chips */}
            <div className="flex items-center gap-1.5">
              {/* Customer Chip */}
              {linkedCustomer ? (
                <div className="flex items-center gap-1.5 pl-1.5 pr-1 py-1 rounded-full text-xs bg-teal-50 border border-teal-200">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center bg-teal-500">
                    <User size={10} className="text-white" />
                  </div>
                  <Tooltip content="View customer details" position="bottom">
                    <button
                      onClick={() => setShowCustomerDetails(true)}
                      className="font-semibold text-slate-700 max-w-[70px] truncate hover:text-teal-700 hover:underline"
                    >
                      {linkedCustomer.name}
                    </button>
                  </Tooltip>
                  <span className="text-slate-400 text-[10px]">
                    {linkedCustomer.phone?.slice(-4)}
                  </span>
                  <Tooltip content="Loyalty Points" position="bottom">
                    <span className="flex items-center gap-0.5 text-amber-600 font-bold bg-amber-50 px-1 py-0.5 rounded">
                      <Award size={10} />
                      {linkedCustomer.loyaltyPoints || 0}
                    </span>
                  </Tooltip>
                  <Tooltip content="View details" position="bottom">
                    <button
                      onClick={() => setShowCustomerDetails(true)}
                      className="p-0.5 hover:bg-teal-100 rounded-full"
                    >
                      <Eye size={10} className="text-teal-600" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Change customer" position="bottom">
                    <button
                      onClick={() => setShowCustomerModal(true)}
                      className="p-0.5 hover:bg-slate-100 rounded-full"
                    >
                      <User size={10} className="text-slate-500" />
                    </button>
                  </Tooltip>
                  <Tooltip content="Remove customer" position="bottom">
                    <button
                      onClick={() => setLinkedCustomer(null)}
                      className="p-0.5 hover:bg-red-100 rounded-full"
                    >
                      <X size={10} className="text-red-400 hover:text-red-600" />
                    </button>
                  </Tooltip>
                </div>
              ) : (
                <Tooltip content="Click to add customer" position="bottom">
                  <button
                    onClick={() => setShowCustomerModal(true)}
                    className="flex items-center gap-1.5 pl-1.5 pr-2 py-1 rounded-full text-xs transition-all bg-slate-100 hover:bg-slate-200 border border-transparent"
                  >
                    <div className="w-5 h-5 rounded-full flex items-center justify-center bg-slate-300">
                      <User size={10} className="text-white" />
                    </div>
                    <span className="text-slate-500">+ Customer</span>
                  </button>
                </Tooltip>
              )}

              {/* Divider */}
              <div className="h-4 w-px bg-slate-200"></div>

              {/* Waiter Chip */}
              {linkedWaiter ? (
                <Tooltip content={`Waiter: ${linkedWaiter.name}`} position="bottom">
                  <button
                    onClick={() => setShowWaiterModal(true)}
                    className="flex items-center gap-1.5 pl-1.5 pr-2 py-1 rounded-full text-xs transition-all bg-teal-50 hover:bg-teal-100 border border-teal-200"
                  >
                    <div className="w-5 h-5 rounded-full flex items-center justify-center bg-teal-500">
                      <ConciergeBell size={10} className="text-white" />
                    </div>
                    <span className="font-semibold text-slate-700 max-w-[60px] truncate">
                      {linkedWaiter.name}
                    </span>
                    <Tooltip content="Remove waiter" position="bottom">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnlinkWaiter();
                        }}
                        className="ml-0.5 p-0.5 hover:bg-red-100 rounded-full"
                      >
                        <X size={10} className="text-red-400 hover:text-red-600" />
                      </button>
                    </Tooltip>
                  </button>
                </Tooltip>
              ) : (
                <Tooltip content="Click to assign waiter" position="bottom">
                  <button
                    onClick={() => setShowWaiterModal(true)}
                    className="flex items-center gap-1.5 pl-1.5 pr-2 py-1 rounded-full text-xs transition-all bg-slate-100 hover:bg-slate-200 border border-transparent"
                  >
                    <div className="w-5 h-5 rounded-full flex items-center justify-center bg-slate-300">
                      <ConciergeBell size={10} className="text-white" />
                    </div>
                    <span className="text-slate-500">+ Waiter</span>
                  </button>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Modern Tab Navigation */}
          <div className="flex gap-1 relative">
            <button
              onClick={() => setActiveTab('kots')}
              className={`flex items-center gap-2 px-6 py-3 font-semibold text-sm transition-all relative ${
                activeTab === 'kots'
                  ? 'text-teal-700'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <ChefHat size={18} />
              <span>Kitchen Orders</span>
              {activeTab === 'kots' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600 rounded-t-full"></div>
              )}
            </button>

            <button
              onClick={() => setActiveTab('bill')}
              className={`flex items-center gap-2 px-6 py-3 font-semibold text-sm transition-all relative ${
                activeTab === 'bill'
                  ? 'text-teal-700'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Receipt size={18} />
              <span>Bill Summary</span>
              {activeTab === 'bill' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600 rounded-t-full"></div>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content - Fixed height with overflow */}
      <div className="flex-1 overflow-y-auto bg-slate-50">
        {activeTab === 'kots' ? (
          <div className="p-4 space-y-4">
            {/* Draft KOT Section - Shows items not yet sent to kitchen */}
            <DraftKOT />

            {/* Sent KOT Orders */}
            <KOTOrders />
          </div>
        ) : (
          <BillSummary items={allBillItems} menuItems={menuItems} loading={loading} />
        )}
      </div>

      {/* Customer Search Modal */}
      <CustomerSearchModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onSelectCustomer={(customer) => {
          setLinkedCustomer(customer);
          setShowCustomerModal(false);
        }}
        linkedCustomer={linkedCustomer}
        onUnlinkCustomer={() => setLinkedCustomer(null)}
      />

      {/* Waiter Selection Modal */}
      <WaiterSelectionModal
        isOpen={showWaiterModal}
        onClose={() => setShowWaiterModal(false)}
        onSelectWaiter={handleAssignWaiter}
        linkedWaiter={linkedWaiter}
        onUnlinkWaiter={handleUnlinkWaiter}
      />

      {/* Customer Details Modal */}
      <CustomerDetailsModal
        isOpen={showCustomerDetails}
        onClose={() => setShowCustomerDetails(false)}
        customer={linkedCustomer}
        showEditButton={false}
      />
    </div>
  );
}
